import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { map, switchMap } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { BREAKPOINTS } from './core/constants/breakpoints.constants';
import { DEFAULT_APP_LANGUAGE } from './core/constants/language.constants';
import { DEFAULT_MOBILE_ROUTE, MOBILE_READY_ROUTES } from './core/enums/route-types.enum';
import { CoverageTimeoutService } from './core/services/coverage/coverage-timeout.service';
import { GoogleTagManagerService } from './core/services/gtm/google-tag-manager.service';
import { LanguageStoreHandlerService } from './core/services/language/language-store-handler.service';
import { UserService } from './core/services/user/user.service';
import { NavbarTreeComponent } from './core/tree/navbar-tree/navbar-tree.component';
import { TreeService } from './core/tree/tree.service';
import { isMobile } from './core/util/feature-detection.helper';
import { HeaderMessageComponent } from './header-message/header-message.component';
import { MaintenanceService } from './maintenance/maintenance.service';
import { ResizeNotifier } from './resize-observer/resize-notifier.service';
import { ConsentModalComponent } from './shared/components/consent-modal/consent-modal.component';
import { ListModeSwitchService } from './shared/components/list-mode-switch/list-mode-switch.service';
import { MaintenanceBannerComponent } from './shared/components/maintenance-banner/maintenance-banner.component';
import { ContainerDirective } from './shared/directives/container/container.directive';
import { BrowserSupportService } from './shared/services/browser-support/browser-support.service';
import { SharedModule } from './shared/shared.module';
import { TermsService } from './terms/terms.service';
import { ToastContainerComponent } from './ui-elements/toast/toast-container/toast-container.component';
import { TooltipComponent } from './ui-elements/tooltip/tooltip.component';
import { UpdateService } from './update.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    NavbarTreeComponent,
    HeaderMessageComponent,
    TooltipComponent,
    ContainerDirective,
    RouterOutlet,
    MaintenanceBannerComponent,
    ConsentModalComponent,
    ToastContainerComponent,
  ],
})
export class AppComponent implements OnInit {
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private termsService = inject(TermsService);
  private languageService = inject(LanguageStoreHandlerService);
  private router = inject(Router);
  private userService = inject(UserService);
  private updateService = inject(UpdateService);
  private swUpdate = inject(SwUpdate);
  private browserSupport = inject(BrowserSupportService);
  private treeService = inject(TreeService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private document = inject<Document>(DOCUMENT);
  private maintenanceService = inject(MaintenanceService);
  private breakpointObserver = inject(BreakpointObserver);
  private gtmService = inject(GoogleTagManagerService);
  private viewportScroller = inject(ViewportScroller);
  private listModeSwitchService = inject(ListModeSwitchService);
  private coverageTimeoutService = inject(CoverageTimeoutService);

  public secondLevelNavVisible: boolean;
  public loggedIn: boolean;
  public isMobile: boolean;

  constructor() {
    const translate = this.translate;
    const router = this.router;
    const swUpdate = this.swUpdate;
    const browserSupport = this.browserSupport;

    this.gtmService.addGtmToDom();

    // show nice page about browser support for all IE browsers
    browserSupport.browserIsSupported$.subscribe((supported) => {
      if (!supported) {
        router.navigate(['/unsupported-browser']);
      }
    });

    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Do a soft redirect to new domain for dev env
        if (this.document.location.hostname.startsWith('dev.pricelist7') || this.document.location.hostname.startsWith('pricelist7')) {
          this.document.location.hostname = this.document.location.hostname.replace('pricelist7', 'nspace');
          return;
        }

        // force SW update check on route change
        if (swUpdate.isEnabled) {
          swUpdate.checkForUpdate();
        }

        if (isMobile() && event.urlAfterRedirects !== DEFAULT_MOBILE_ROUTE) {
          const isWideScreen = this.breakpointObserver.isMatched(`(min-width: ${BREAKPOINTS.XL})`);
          if (!MOBILE_READY_ROUTES.some((route) => event.urlAfterRedirects.indexOf(`/${route}`) > -1) && !isWideScreen) {
            this.router.navigate([DEFAULT_MOBILE_ROUTE]);

            return;
          }
        }

        window.dataLayer = window.dataLayer || [];

        this.userService
          .getUser()
          .pipe(switchMap((user) => this.userService.getUserCompany().pipe(map((company) => ({ user, company })))))
          .subscribe(({ user, company }) => {
            if (user && company) {
              window.dataLayer.push({
                event: 'routeView',
                url: event.urlAfterRedirects || window.location.href,
                company: company.name,
                user_id: user.id,
                role: user.role.name,
                ax: company.axClientCode,
              });
            }
          });
      }
    });
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang(DEFAULT_APP_LANGUAGE);

    this.treeService.getState().subscribe((val) => {
      this.secondLevelNavVisible = val.first?.children?.length > 0;
    });
  }

  ngOnInit(): void {
    this.isMobile = isMobile();

    this.authService.authenticatedObservable().subscribe((user) => {
      this.listModeSwitchService.initFromLocalStorage();
      if (user) {
        this.maintenanceService.fetchPlan();
      }

      this.loggedIn = user;
      this.changeDetectorRef.detectChanges();
    });

    this.viewportScroller.setOffset(() => {
      const topOffset = ResizeNotifier.getCurrentValue().navbarBorderBox?.height || 0;
      return [0, topOffset + 16];
    });
  }
}
